import React, { useState, useEffect, useRef } from 'react';
import {
	TextField,
	CircularProgress,
	Popper,
	Paper,
	ClickAwayListener
} from '@mui/material';
import PropTypes from 'prop-types';
import { Box } from '@mui/system';
import { fetchLocationFromPincode, getPincodeDetailsById } from '../../Services/api-config.js';
import {
	CommonTextInputStyles,
	formatValueToNumbers,
	getErrorMessage
} from '../../utils/util.js';
import { TextField as Txt } from './textfield.js';
import Loading from './LoaderComponent/loader.js';

const PincodeSelector = ({
	onSelect,
	onError,
	setOptions,
	options,
	pincodeInput,
	setPincodeInput,
	selectedLocation,
	setSelectedLocation,
	errorFromComponent,
	handleResetLocationDetails = () => {},
	editingPincodeId = '',
	pincodeError,
	resetPincodeId = () => {},
	showPlaceholder = true
}) => {
	const [localPincodeInput, setLocalPincodeInput] = useState(pincodeInput);
	const debounceRef = useRef(null);
	const [searchInput, setSearchInput] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [openPopper, setOpenPopper] = useState(false);
	const [focusedIndex, setFocusedIndex] = useState(-1);

	const anchorRef = useRef(null);
	const debounceTimeoutRef = useRef(null);
	const abortControllerRef = useRef(null);
	const optionRefs = useRef([]);

	const fetchLocationByPincode = async (pincode, showPopper = true) => {
		if (pincode.length !== 6) {
			return;
		}

		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		const controller = new AbortController();
		abortControllerRef.current = controller;

		setLoading(true);
		try {
			const res = await fetchLocationFromPincode(pincode, controller.signal);
			if (res?.status === 200) {
				const locations = res.data;
				setOptions(locations);
				if (showPopper) {
					setError(false);
					setErrorMessage('');
					handleResetLocationDetails();

					if (locations.length === 1) {
						const onlyOption = locations[0];
						setSelectedLocation(onlyOption);
						onSelect(onlyOption);
						setOpenPopper(false);
						setSearchInput('');
					} else {
						setSelectedLocation(null);
						setOpenPopper(true);
					}
				}
			} else if (res) {
				handleError(res);
			}
		} catch (error_) {
			handleError(error_);
		} finally {
			setLoading(false);
		}
	};

	const handleError = (err) => {
		const message = getErrorMessage(err);
		setErrorMessage(message);
		setError(true);
		setOptions([]);
		setSearchInput('');
		setSelectedLocation(null);
		setOpenPopper(false);
		onError?.();
	};

	useEffect(() => {
		setErrorMessage(pincodeError);
		setError(true);
	}, [pincodeError, pincodeInput]);

	const fetchById = async () => {
		try {
			const res = await getPincodeDetailsById(editingPincodeId);
			if (res?.status === 200) {
				const location = res.data;
				const locationDetails = {
					localityName: location?.localityName,
					subDistrictName: location?.subDistrictName,
					districtName: location?.districtName,
					stateName: location?.stateName,
					pincodeId: editingPincodeId
				};
				setSelectedLocation(location);
				setPincodeInput(location.pincode);
				setLocalPincodeInput(location.pincode);
				setOptions([locationDetails]);
				onSelect(locationDetails, location.pincode);
				setOpenPopper(false);
				setError(false);
				setErrorMessage('');
				fetchLocationByPincode(location.pincode, false);
				resetPincodeId();
			} else {
				handleError(res);
			}
		} catch (error_) {
			handleError(error_);
		}
	};

	const debouncedFetchLocation = (value) => {
		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}

		debounceTimeoutRef.current = setTimeout(() => {
			fetchLocationByPincode(value);
		}, 500);
	};

	useEffect(() => {
		if (editingPincodeId) {
			fetchById();
		}
	}, [editingPincodeId]);

	useEffect(() => {
		if (pincodeInput?.length === 6 && !selectedLocation && !editingPincodeId) {
			debouncedFetchLocation(pincodeInput);
		} else {
			setErrorMessage('');
			setError(false);
			setSearchInput('');
			setOpenPopper(false);
		}
	}, [pincodeInput]);

	useEffect(() => {
		if (focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
			optionRefs.current[focusedIndex].scrollIntoView({ block: 'nearest' });
		}
	}, [focusedIndex]);

	useEffect(() => {
		setLocalPincodeInput(pincodeInput);
	}, [pincodeInput, pincodeInput]);

	const handleOptionClick = (option) => {
		setSelectedLocation(option);
		onSelect(option);
		setOpenPopper(false);
		setSearchInput('');
		setErrorMessage('');
		setError(false);
		setFocusedIndex(-1);
	};

	const handleKeyDown = (e) => {
		if (!openPopper || options.length === 0) {
			return;
		}

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setFocusedIndex((prev) => (prev + 1) % options.length);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setFocusedIndex((prev) => (prev - 1 + options.length) % options.length);
		} else if (e.key === 'Enter' && focusedIndex >= 0) {
			handleOptionClick(options[focusedIndex]);
		} else if (e.key === 'Escape') {
			setOpenPopper(false);
		}
	};

	const getBackgroundColor = (isFocused, isSelected) => {
		if (isFocused) {
			return '#e6f7e6';
		}

		if (isSelected) {
			return '#f9fcf0';
		}

		return 'white';
	};

	// Usage:

	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: '16px',
				width: '100%'
			}}
		>
			<Loading open={loading} />
			<Box
				ref={anchorRef}
				width='100%'
				onFocus={() => {
					if (pincodeInput.length === 6 && options.length > 1) {
						setOpenPopper(true);
					}
				}}
				onKeyDown={handleKeyDown}
			>
				<Txt
					value={localPincodeInput}
					{...{ ...CommonTextInputStyles, width: '100%' }}
					handleInput={(value) => {
						const temp = formatValueToNumbers(value);
						if (temp.length <= 6) {
							setLocalPincodeInput(temp);
							if (debounceRef.current) {
								clearTimeout(debounceRef.current);
							}

							debounceRef.current = setTimeout(() => {
								if (temp !== pincodeInput) {
									setPincodeInput(temp);
								}

								if (temp.length < 6) {
									handleResetLocationDetails();
								}
							}, 300);
						}
					}}
					placeholder={showPlaceholder ? 'pincode' : ''}
					error={errorFromComponent || (error && errorMessage) || ''}
					autoComplete={'false'}
				/>

				{openPopper && (
					<Popper
						open={openPopper}
						anchorEl={anchorRef.current}
						placement='bottom-start'
						style={{
							zIndex: 1300,
							width: anchorRef.current
								? anchorRef.current.clientWidth
								: undefined,
							boxShadow: 'none',
							border: '1px solid #ECF0E5'
						}}
					>
						<ClickAwayListener
							onClickAway={() => {
								setSearchInput('');
								setOpenPopper(false);
							}}
						>
							<Paper
								elevation={3}
								sx={{ width: '100%', p: 1, boxShadow: 'none' }}
							>
								<TextField
									fullWidth
									placeholder='Search location'
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									size='small'
									sx={{ mb: 1 }}
									slotProps={{
										htmlInput: { 'aria-label': 'Search location' }
									}}
								/>
								<div style={{ maxHeight: '200px', overflowY: 'auto' }}>
									{loading ? (
										<div style={{ padding: '10px', textAlign: 'center' }}>
											<CircularProgress size={20} />
										</div>
									) : (
										options
											.filter((option) =>
												`${option.localityName} ${option.subDistrictName} ${option.districtName} ${option.stateName}`
													.toLowerCase()
													.includes(searchInput.toLowerCase())
											)
											.map((option, index) => {
												const isSelected
													= selectedLocation?.pincodeId === option.pincodeId;
												const isFocused = index === focusedIndex;

												return (
													<Box
														ref={(el) => {
															optionRefs.current[index] = el;
														}}
														key={option.pincodeId}
														aria-selected={isSelected}
														onClick={() => handleOptionClick(option)}
														style={{
															padding: '8px 12px',
															cursor: 'pointer',
															backgroundColor: getBackgroundColor(isFocused, isSelected),
															fontWeight: 'normal'
														}}
													>
														{`${option.localityName || ''} - ${option.subDistrictName || ''}, ${option.districtName || ''} (${option.stateName || ''})`}
													</Box>
												);
											})
									)}
								</div>
							</Paper>
						</ClickAwayListener>
					</Popper>
				)}
			</Box>
		</div>
	);
};

PincodeSelector.propTypes = {
	onSelect: PropTypes.func.isRequired,
	onError: PropTypes.func,
	setPincodeInput: PropTypes.func.isRequired,
	pincodeInput: PropTypes.string.isRequired,
	options: PropTypes.array.isRequired,
	setOptions: PropTypes.func.isRequired,
	selectedLocation: PropTypes.object,
	setSelectedLocation: PropTypes.func.isRequired,
	handleResetLocationDetails: PropTypes.func,
	errorFromComponent: PropTypes.any,
	editingPincodeId: PropTypes.string,
	resetPincodeId: PropTypes.func
};

export default React.memo(PincodeSelector);
